import { useEffect, useRef, useState } from "react";
import {
  Check,
  ChevronDown,
  Copy,
  Download,
  RotateCcw,
  Send,
  ThumbsDown,
  ThumbsUp,
  Trash2,
} from "lucide-react";

import { DinnoxBot } from "@/components/guide/dinnox-bot";
import { RichText } from "@/components/guide/rich-text";
import { downloadTranscriptPdf } from "@/components/guide/transcript-pdf";
import { useDinnoxHidden } from "@/components/guide/use-dinnox-visibility";
import {
  useAskGuide,
  useClearGuide,
  useDeleteGuideMessage,
  useGuide,
  useRateGuideMessage,
  type GuideMessage,
  type GuideRating,
} from "@/lib/guide";
import { cn } from "@/lib/utils";

/**
 * Dinnobot — the read-only, in-app help assistant, fronted by a friendly robot
 * mascot that floats in the bottom-right corner of every page (see
 * ``GUIDE-CHATBOT-PLAYBOOK.md`` + the Dinnobot rebrand delta). It answers "how do
 * I use HRIS Workbench?" questions, grounded server-side. Distinct from the AI
 * Copilot, and it hides itself entirely when the deployment has no Anthropic key
 * configured (``available: false``).
 *
 * Code names stay ``Guide`` (app, routes, hooks, component) — only what the user
 * sees says "Dinnobot".
 */

interface Turn {
  id: string;
  role: "user" | "guide";
  body: string;
  rating?: GuideRating;
  error?: boolean;
}

const SUGGESTED = [
  "How do I add an employee?",
  "How do I file a leave request?",
  "Where do I approve requests?",
  "How do I run payroll?",
];

let localId = 0;
const nextId = () => `guide-local-${(localId += 1)}`;

/** Only server-persisted turns (real UUIDs) can be rated or deleted; optimistic
 *  and error bubbles carry a local id and expose no such actions. */
const isServerId = (id: string) => !id.startsWith("guide-local-");

const toTurn = (m: GuideMessage): Turn => ({
  id: m.id,
  role: m.role,
  body: m.body,
  rating: m.rating ?? "",
});

export function GuideWidget() {
  const thread = useGuide();
  const ask = useAskGuide();
  const clear = useClearGuide();
  const rate = useRateGuideMessage();
  const del = useDeleteGuideMessage();
  const { hidden } = useDinnoxHidden();

  const [open, setOpen] = useState(false);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [input, setInput] = useState("");
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const seeded = useRef(false);
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const available = thread.data?.available ?? false;
  const pending = ask.isPending;

  // Seed the transcript from the server thread once it loads (so reopening
  // shows prior turns). Only seeds when we have nothing local yet.
  useEffect(() => {
    if (!seeded.current && thread.data?.messages) {
      setTurns(thread.data.messages.map(toTurn));
      seeded.current = true;
    }
  }, [thread.data]);

  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [turns, pending, open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  async function send(text: string) {
    const body = text.trim();
    if (!body || pending) return;
    setInput("");
    const tempId = nextId();
    setTurns((prev) => [...prev, { id: tempId, role: "user", body }]);
    try {
      const result = await ask.mutateAsync(body);
      // Swap the optimistic turn for the two server turns (real ids), so both
      // the question and the reply can then be rated / deleted.
      setTurns((prev) => [
        ...prev.filter((t) => t.id !== tempId),
        toTurn(result.user_message),
        toTurn(result.reply),
      ]);
    } catch {
      setTurns((prev) => [
        ...prev,
        {
          id: nextId(),
          role: "guide",
          body: "Sorry — I couldn't answer just now. Please try again.",
          error: true,
        },
      ]);
    }
  }

  async function handleClear() {
    if (clear.isPending) return;
    setTurns([]);
    try {
      await clear.mutateAsync();
    } catch {
      /* server clear failed; the local view is already reset */
    }
  }

  async function handleRate(turn: Turn, value: "up" | "down") {
    const next: GuideRating = turn.rating === value ? "" : value;
    const before = turn.rating ?? "";
    setTurns((prev) =>
      prev.map((t) => (t.id === turn.id ? { ...t, rating: next } : t)),
    );
    try {
      await rate.mutateAsync({ id: turn.id, rating: next });
    } catch {
      setTurns((prev) =>
        prev.map((t) => (t.id === turn.id ? { ...t, rating: before } : t)),
      );
    }
  }

  async function handleCopy(turn: Turn) {
    try {
      await navigator.clipboard.writeText(turn.body);
      setCopiedId(turn.id);
      window.setTimeout(
        () => setCopiedId((id) => (id === turn.id ? null : id)),
        1500,
      );
    } catch {
      /* clipboard unavailable (insecure context) — nothing to do */
    }
  }

  async function handleDelete(turn: Turn) {
    try {
      const { deleted_ids } = await del.mutateAsync(turn.id);
      const removed = new Set(deleted_ids);
      setTurns((prev) => prev.filter((t) => !removed.has(t.id)));
    } catch {
      /* leave the transcript as-is on failure */
    }
  }

  /** Re-ask the question that produced the reply at ``index``. */
  function askAgain(index: number) {
    for (let i = index - 1; i >= 0; i -= 1) {
      if (turns[i].role === "user") {
        send(turns[i].body);
        return;
      }
    }
  }

  function handleDownload() {
    downloadTranscriptPdf(turns.map(({ role, body }) => ({ role, body })));
  }

  // Hide entirely while loading or when Dinnobot isn't configured for this
  // deployment — no broken button in a key-less environment (playbook §13) —
  // or when the user has switched Dinnobot off from the top bar.
  if (!available || hidden) return null;

  return (
    <>
      {/* Floating mascot launcher — only while the chat is closed. Transparent
          SVG, so it floats with a drop-shadow (no box). */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Open Dinnobot — how to use HRIS Workbench"
          className={cn(
            "fixed bottom-4 right-4 z-40 transition-transform hover:scale-110 active:scale-95",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-blue/50 focus-visible:ring-offset-2 rounded-full",
            "print:hidden",
          )}
        >
          <DinnoxBot
            className="h-14 w-auto drop-shadow-lg sm:h-16"
            title="Open Dinnobot"
          />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          role="dialog"
          aria-label="Dinnobot — how to use HRIS Workbench"
          className={cn(
            "fixed bottom-4 right-4 z-40 flex flex-col overflow-hidden print:hidden",
            "h-[min(34rem,calc(100vh-6rem))] w-[min(24rem,calc(100vw-2rem))]",
            "rounded-card border border-slate-200 bg-white shadow-2xl",
          )}
        >
          {/* Header — mascot avatar + name + "Support" subheader */}
          <header className="flex items-center gap-3 border-b border-slate-200 bg-navy px-4 py-3 text-white">
            <span className="grid size-9 shrink-0 place-items-center overflow-hidden rounded-full bg-white/15">
              <DinnoxBot variant="avatar" className="size-full" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold leading-tight">Dinnobot</p>
              <p className="truncate text-[11px] text-white/70">
                Support · how to use HRIS Workbench
              </p>
            </div>
            {turns.length > 0 && (
              <>
                <button
                  type="button"
                  onClick={handleDownload}
                  aria-label="Download conversation as PDF"
                  title="Download conversation as PDF"
                  className="rounded-md p-1.5 text-white/70 hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                >
                  <Download size={16} strokeWidth={1.75} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={handleClear}
                  aria-label="Clear conversation"
                  title="Clear conversation"
                  className="rounded-md p-1.5 text-white/70 hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                >
                  <Trash2 size={16} strokeWidth={1.75} aria-hidden="true" />
                </button>
              </>
            )}
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Minimize Dinnobot"
              title="Minimize (your conversation is kept)"
              className="rounded-md p-1.5 text-white/70 hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
            >
              <ChevronDown size={18} strokeWidth={1.75} aria-hidden="true" />
            </button>
          </header>

          {/* Transcript */}
          <div className="flex-1 space-y-3 overflow-y-auto bg-bg px-3 py-3">
            {turns.length === 0 && (
              <div className="rounded-card border border-slate-200 bg-white p-3">
                <p className="flex items-center gap-2 text-sm font-medium text-navy">
                  <span className="grid size-7 shrink-0 place-items-center overflow-hidden rounded-full bg-blue/10 ring-1 ring-blue/15">
                    <DinnoxBot variant="avatar" className="size-full" />
                  </span>
                  Hi — I&apos;m Dinnobot 👋
                </p>
                <p className="mt-1 text-xs text-slate">
                  I&apos;m the friendly robot down here. I explain how to use HRIS
                  Workbench — I don&apos;t change anything or read your HR data.
                  Try one of these:
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {SUGGESTED.map((prompt) => (
                    <button
                      key={prompt}
                      type="button"
                      onClick={() => send(prompt)}
                      className={cn(
                        "rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-navy",
                        "hover:border-blue/40 hover:text-blue",
                      )}
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {turns.map((turn, i) => (
              <div key={turn.id}>
                <Bubble turn={turn} />
                {turn.role === "guide" && !turn.error && isServerId(turn.id) && (
                  <ReplyActions
                    turn={turn}
                    copied={copiedId === turn.id}
                    onRate={(value) => handleRate(turn, value)}
                    onCopy={() => handleCopy(turn)}
                    onAskAgain={() => askAgain(i)}
                    onDelete={() => handleDelete(turn)}
                    disabled={pending}
                  />
                )}
              </div>
            ))}

            {pending && (
              <div className="flex items-center gap-2 px-1 text-xs text-slate">
                <span className="grid size-7 shrink-0 place-items-center overflow-hidden rounded-full bg-blue/10 ring-1 ring-blue/15">
                  <DinnoxBot variant="avatar" className="size-full" />
                </span>
                <span className="inline-flex gap-1">
                  <Dot /> <Dot /> <Dot />
                </span>
                Looking that up…
              </div>
            )}
            <div ref={endRef} />
          </div>

          {/* Composer */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send(input);
            }}
            className="flex items-end gap-2 border-t border-slate-200 bg-white p-2"
          >
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send(input);
                }
              }}
              rows={1}
              disabled={pending}
              placeholder="Ask Dinnobot how to do something…"
              aria-label="Ask Dinnobot"
              className="max-h-28 flex-1 resize-none rounded-md bg-transparent px-2 py-1.5 text-sm text-navy placeholder:text-slate focus:outline-none disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={pending || !input.trim()}
              aria-label="Send"
              className={cn(
                "flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-blue text-white",
                "hover:bg-blue-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-blue/50",
                "disabled:opacity-40",
              )}
            >
              <Send size={15} strokeWidth={1.75} aria-hidden="true" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}

function Bubble({ turn }: { turn: Turn }) {
  const isUser = turn.role === "user";
  if (isUser) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-card bg-blue px-3 py-2 text-sm text-white">
          <p className="whitespace-pre-wrap">{turn.body}</p>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-end justify-start gap-2">
      <span className="mb-0.5 grid size-8 shrink-0 place-items-center overflow-hidden rounded-full bg-blue/10 shadow-sm ring-1 ring-blue/15">
        <DinnoxBot variant="avatar" className="size-full" title="Dinnobot" />
      </span>
      <div
        className={cn(
          "max-w-[80%] rounded-card px-3 py-2 text-sm",
          turn.error
            ? "border border-red-200 bg-red-50 text-red-700"
            : "border border-slate-200 bg-white text-navy",
        )}
      >
        {turn.error ? (
          <p className="whitespace-pre-wrap">{turn.body}</p>
        ) : (
          <RichText text={turn.body} />
        )}
      </div>
    </div>
  );
}

/** The action row under a Dinnobot reply: rate 👍/👎, copy, ask again, delete. */
function ReplyActions({
  turn,
  copied,
  onRate,
  onCopy,
  onAskAgain,
  onDelete,
  disabled,
}: {
  turn: Turn;
  copied: boolean;
  onRate: (value: "up" | "down") => void;
  onCopy: () => void;
  onAskAgain: () => void;
  onDelete: () => void;
  disabled?: boolean;
}) {
  const iconBtn =
    "rounded p-1 text-slate hover:bg-slate-100 hover:text-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-blue/40 disabled:opacity-40";
  return (
    <div className="mt-1 flex items-center gap-0.5 pl-10">
      <button
        type="button"
        onClick={() => onRate("up")}
        aria-label="Helpful"
        aria-pressed={turn.rating === "up"}
        title="Helpful"
        className={cn(iconBtn, turn.rating === "up" && "bg-blue/10 text-blue")}
      >
        <ThumbsUp size={14} strokeWidth={1.75} aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={() => onRate("down")}
        aria-label="Not helpful"
        aria-pressed={turn.rating === "down"}
        title="Not helpful"
        className={cn(
          iconBtn,
          turn.rating === "down" && "bg-red-50 text-red-600",
        )}
      >
        <ThumbsDown size={14} strokeWidth={1.75} aria-hidden="true" />
      </button>
      <span className="mx-0.5 h-3 w-px bg-slate-200" aria-hidden="true" />
      <button
        type="button"
        onClick={onCopy}
        aria-label="Copy reply"
        title={copied ? "Copied" : "Copy reply"}
        className={cn(iconBtn, copied && "text-green-600")}
      >
        {copied ? (
          <Check size={14} strokeWidth={1.75} aria-hidden="true" />
        ) : (
          <Copy size={14} strokeWidth={1.75} aria-hidden="true" />
        )}
      </button>
      <button
        type="button"
        onClick={onAskAgain}
        disabled={disabled}
        aria-label="Ask again"
        title="Ask again"
        className={iconBtn}
      >
        <RotateCcw size={14} strokeWidth={1.75} aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={onDelete}
        aria-label="Delete this exchange"
        title="Delete this exchange"
        className={cn(iconBtn, "hover:bg-red-50 hover:text-red-600")}
      >
        <Trash2 size={14} strokeWidth={1.75} aria-hidden="true" />
      </button>
    </div>
  );
}

function Dot() {
  return <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-slate" />;
}
